import { useMemo } from "react";

import {
  dateRange6MonthsAgo,
  dateRange30DaysAgo,
  SelectDateRangeState,
} from "../../../../components/SelectDateRange/SelectDateRange";
import {
  useEnumSearchParam,
  useSearchParam,
  useSelectDateRangeParams,
  useStringArrayParam,
} from "../../../../hooks/useSearchParam";
import { formatDateRange } from "../../../../utils/datetime";
import {
  AnalyticsDimension,
  CompensationExtractionCategory,
  CompensationExtractionCategoryType,
  CompensationExtractionRateType,
} from "../../../graphql";
import useFeatureFlag from "../../../graphql/hooks/useFeatureFlag";
import { MultiSelect, SingleSelect } from "../types";
import useAnalyticsContext from "../useAnalyticsContext";
import { isDimensionSupportedForOrg } from "../utils";

export type CompensationCategoryOption =
  | CompensationExtractionCategoryType
  | "ALL";

export const FRIENDLY_CATEGORY_NAMES: Record<
  CompensationExtractionCategory,
  string
> = {
  INTERVIEWER_STATED: "Interviewer stated",
  CANDIDATE_CURRENT: "Candidate current",
  CANDIDATE_EXPECTED: "Candidate expected",
};

const RATE_OPTIONS = [
  { label: "Annual Salary", value: CompensationExtractionRateType.Annual },
  { label: "Hourly Rate", value: CompensationExtractionRateType.Hourly },
];

export type CompensationTrendsConfig = {
  primaryDimension: SingleSelect<AnalyticsDimension>;
  secondaryDimension: SingleSelect<AnalyticsDimension>;
  positions: MultiSelect;
  locations: MultiSelect;
  departments: MultiSelect;
  interviewers: MultiSelect;
  stages: MultiSelect;
  category: SingleSelect<CompensationCategoryOption>;
  currency: SingleSelect<string>;
  rate: SingleSelect<CompensationExtractionRateType>;
  dateRange: {
    value: SelectDateRangeState;
    displayValue: string;
    setValue(value: SelectDateRangeState | undefined): void;
  };
  filters: {
    positions: string[];
    locations: string[];
    departments: string[];
    interviewers: string[];
    stages: string[];
  };
  enabledFilters: {
    positions: boolean;
    locations: boolean;
    departments: boolean;
    interviewers: boolean;
    stages: boolean;
  };
};

/**
 * Manages the state of user-selectable analytics configurations such as
 * filters, dimensions, and chart settings.
 *
 * Only manages the config, but not the query execution.
 */
const useCompensationTrendsConfig = (
  defaultDateRange = dateRange6MonthsAgo
): CompensationTrendsConfig => {
  const [positions, setPositions] = useStringArrayParam("positions");
  const [locations, setLocations] = useStringArrayParam("locations");
  const [departments, setDepartments] = useStringArrayParam("departments");
  const default30Days = useFeatureFlag("advanced-insights:default-30-days");
  const [dateRange, setDateRange] = useSelectDateRangeParams(
    "dateRange",
    default30Days ? dateRange30DaysAgo() : defaultDateRange()
  );
  const [category, setCategory] =
    useEnumSearchParam<CompensationCategoryOption>("category", "ALL");
  const [currency, setCurrency] = useSearchParam("currency", "");
  const [rate, setRate] = useSearchParam(
    "rate",
    CompensationExtractionRateType.Annual
  );

  const { atsDataState } = useAnalyticsContext();
  const locationSupported = isDimensionSupportedForOrg(
    AnalyticsDimension.Location,
    atsDataState
  );

  const filters = {
    positions: positions || [],
    locations: locations || [],
    departments: departments || [],
    interviewers: [],
    stages: [],
  };

  const displayDateRangeValue = useMemo(() => {
    if (dateRange) {
      return formatDateRange(dateRange.start, dateRange.end);
    }
    return "";
  }, [dateRange]);

  return {
    primaryDimension: {
      value: AnalyticsDimension.None,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      setValue: () => {},
      options: [],
    },
    secondaryDimension: {
      value: AnalyticsDimension.None,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      setValue: () => {},
      options: [],
    },
    positions: {
      values: positions,
      setValues: setPositions,
    },
    locations: {
      values: locations,
      setValues: setLocations,
    },
    departments: {
      values: departments,
      setValues: setDepartments,
    },
    category: {
      value: category || "ALL",
      setValue: setCategory,
      options: [],
    },
    currency: {
      value: currency || "",
      setValue: setCurrency,
      options: [],
    },
    rate: {
      value: rate as CompensationExtractionRateType,
      setValue: setRate,
      options: RATE_OPTIONS,
    },
    interviewers: {
      values: [],
      setValues: () => 0,
    },
    stages: {
      values: [],
      setValues: () => 0,
    },
    dateRange: {
      value: dateRange,
      displayValue: displayDateRangeValue,
      setValue: setDateRange,
    },
    filters,
    enabledFilters: {
      positions: true,
      locations: locationSupported,
      departments: true,
      interviewers: false,
      stages: false,
    },
  };
};

export default useCompensationTrendsConfig;
